import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import { useToast } from '@components/Common/Toast/ToastProvider';
import type { EmailTemplate } from '@components/EmailTemplates/types';
import { sanitizeHtml } from '@helpers/utils';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Box, Button, Chip, Typography } from '@mui/material';
import dayjs from '@helpers/date';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  fetchDripSequences,
  fetchFireLog,
  fetchRecentSends,
  fetchRegistrySources,
  fetchTemplate,
  fetchTrend,
  setRulePaused,
  type DripSequence,
  type FireLogEntry,
  type SendRecord,
  type TrendBucket,
} from './api';
import './index.scss';
import {
  buildRegistry,
  FAMILY_LABELS,
  formatRateFraction,
  INTERNAL_AUDIENCE,
  isEnabledButNeverFired,
  whenItFires,
  withRuleStatus,
  type EmailRegistryRow,
} from './registry';
import { formatDayMonth } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';

const TABS: WorkspaceTab[] = [
  { id: 'content', label: 'Content', icon: <ArticleOutlinedIcon /> },
  { id: 'when', label: 'When it sends', icon: <ScheduleOutlinedIcon /> },
  { id: 'performance', label: 'Performance', icon: <BarChartOutlinedIcon /> },
  { id: 'sends', label: 'Recent sends', icon: <SendOutlinedIcon /> },
];

const STATUS_CLASS: Record<string, string> = {
  Active: 'is-active',
  Disabled: 'is-disabled',
  'No trigger': 'is-no-trigger',
  'Never sent': 'is-never-sent',
};

/** The audit endpoint returns "" rather than null for missing fields; do not hand those to dayjs. */
const fmtWhen = (d?: string | null) => (d && d.trim() ? dayjs(d).fromNow() : '—');

/**
 * One email, as a record.
 *
 * Content, the thing that fires it, how it has performed and who recently received it — the four
 * questions that used to be spread across three tabs and, in the case of the owning rule, not
 * linked from the template at all.
 */
const EmailRecord: React.FC = () => {
  const { templateKey = '' } = useParams<{ templateKey: string }>();
  const navigate = useNavigate();

  const [row, setRow] = useState<EmailRegistryRow | null>(null);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [sequences, setSequences] = useState<DripSequence[]>([]);
  const [trend, setTrend] = useState<TrendBucket[]>([]);
  const [fireLog, setFireLog] = useState<FireLogEntry[]>([]);
  const [sends, setSends] = useState<SendRecord[]>([]);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'content');
  const [activityOpen, toggleActivity] = useActivityRail();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The registry sources are needed for the rule join and the 30-day rates; the rest are
      // per-record. All in parallel — the analytics call is the expensive one and there is exactly
      // one of it.
      const [sources, tpl, drip] = await Promise.all([
        fetchRegistrySources(),
        fetchTemplate(templateKey),
        fetchDripSequences().catch(() => ({ sequences: [], stats: {} })),
      ]);
      const built = buildRegistry(sources.templates, sources.rules, sources.stats).find(
        (r) => r.templateKey === templateKey,
      );
      if (!built) {
        setError('That email template no longer exists.');
        return;
      }
      setRow(built);
      setTemplate(tpl);
      setSequences(drip.sequences ?? []);

      // Secondary panels are best-effort: a failing trend must not blank the whole record.
      const [t, f, s] = await Promise.all([
        fetchTrend(templateKey).catch(() => []),
        built.family === 'automation' || built.rule
          ? fetchFireLog(templateKey).catch(() => [])
          : Promise.resolve([]),
        fetchRecentSends(templateKey).catch(() => []),
      ]);
      setTrend(t);
      setFireLog(f);
      setSends(s);
    } catch {
      setError('Could not load this email. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [templateKey]);

  useEffect(() => {
    load();
  }, [load]);

  const dripStepInfo = useMemo(() => {
    if (!row || row.family !== 'drip') return null;
    for (const seq of sequences) {
      const step = seq.steps.find((s) => s.templateKey === row.templateKey);
      if (step) return { sequence: seq, step };
    }
    return null;
  }, [row, sequences]);

  const glance: GlanceRow[] = useMemo(() => {
    if (!row) return [];
    return [
      { key: 'Audience', value: row.audience },
      { key: 'When it fires', value: whenItFires(row, dripStepInfo?.step.dayOffset) },
      { key: 'Sends (30d)', value: String(row.sent), mono: true },
      {
        key: 'Open / Click',
        // Internal sends carry no tracking pixel, so 0% would read as a failure.
        value:
          row.audience === INTERNAL_AUDIENCE
            ? 'Not tracked'
            : `${formatRateFraction(row.openRate)} / ${formatRateFraction(row.clickRate)}`,
        mono: row.audience !== INTERNAL_AUDIENCE,
      },
    ];
  }, [row, dripStepInfo]);

  /**
   * Activity is the rule fire log — successes and skips both. Only automation emails have one:
   * transactional sends are called straight from application code and never pass through the rule
   * engine, and drip steps are walked by the scheduler.
   */
  const timeline: TimelineEntry[] = useMemo(
    () =>
      fireLog.map((f, i) => {
        const result = (f.result ?? 'SUCCESS').toUpperCase();
        return {
          id: f.id || `f-${i}`,
          kind: 'email' as const,
          text: result === 'SUCCESS' ? 'Queued' : `${result[0]}${result.slice(1).toLowerCase()}${f.reason ? ` — ${f.reason.toLowerCase()}` : ''}`,
          meta: `${fmtWhen(f.firedAt)}${f.ruleId ? ` · ${f.ruleId}` : ''}`,
        };
      }),
    [fireLog],
  );

  /**
   * Pause or resume the rule from the record. A transactional template with a rule (int-1, the
   * weekly referral summary) has no other switch on this screen, and the rule IS its switch: the
   * scheduled job checks the rule's status before it sends.
   */
  const toggleRulePaused = async () => {
    if (!row?.rule?.id) return;
    const pausing = (row.rule.status ?? '').toUpperCase() === 'ACTIVE';
    setRuleSaving(true);
    try {
      const updated = await setRulePaused(row.rule.id, pausing);
      setRow((prev) => (prev ? withRuleStatus(prev, pausing, updated) : prev));
      showToast(pausing ? 'Rule paused' : 'Rule resumed', 'success');
    } catch {
      showToast(pausing ? 'Could not pause the rule' : 'Could not resume the rule', 'error');
    } finally {
      setRuleSaving(false);
    }
  };

  if (loading) return <SkeletonLoader variant="workspace" />;
  if (error) return <ErrorMessage page message={error} />;
  if (!row || !template) return <ErrorMessage page message="Email not found." />;

  const identityRail = (
    <IdentityRail
      audit={{
        createdAt: (template as unknown as { createdAt?: string }).createdAt,
        createdBy: undefined,
        updatedAt: template.updatedAt ?? undefined,
        updatedBy: undefined,
      }}
      badges={
        <Chip
          className={`eo-chip eo-family-${row.family}`}
          label={FAMILY_LABELS[row.family]}
          size="small"
        />
      }
      glance={glance}
      initials={row.family === 'drip' ? 'DR' : row.family === 'automation' ? 'AU' : 'TX'}
      name={row.name}
      statusChip={
        <Chip className={`eo-chip ${STATUS_CLASS[row.status]}`} label={row.status} size="small" />
      }
      subId={row.templateKey}
    />
  );

  const contentTab = (
    <>
      <SectionCard icon={<ArticleOutlinedIcon />} title="Subject and preview">
        <WorkspaceField full label="Subject" value={template.subject || '-'} />
        <WorkspaceField full label="Preview text" value={template.previewText || '-'} />
      </SectionCard>

      <SectionCard
        icon={<ArticleOutlinedIcon />}
        meta="Read-only"
        title="HTML body"
      >
        <Box sx={{ width: '100%' }}>
          {/* A preview, not the editor. The editor keeps its own screen — source, live preview and
              test-send — and is linked to rather than duplicated here. */}
          <Box
            className="eo-html-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(template.htmlContent || '') }}
          />
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            variant="outlined"
            onClick={() => navigate('/email-campaigns/templates')}
          >
            Open in template editor
          </Button>
        </Box>
      </SectionCard>

      <SectionCard icon={<ArticleOutlinedIcon />} title="Available variables">
        <Box className="eo-variables">
          {(template.availableVariables ?? []).length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              This template takes no variables.
            </Typography>
          ) : (
            template.availableVariables.map((v) => (
              <Chip key={v} className="eo-variable" label={v} size="small" />
            ))
          )}
        </Box>
      </SectionCard>
    </>
  );

  const whenTab = (
    <>
      {row.family !== 'drip' && row.rule && (
        <SectionCard
          icon={<ScheduleOutlinedIcon />}
          meta={row.rule.ruleId}
          title="Automation rule"
        >
          <WorkspaceField
            label="Rule status"
            value={humanizeEnum(row.rule.status)}
          />
          <WorkspaceField label="Trigger type" value={row.rule.triggerType || '-'} />
          <WorkspaceField label="Schedule" value={row.rule.scheduleType || '-'} />
          <WorkspaceField label="Priority" value={row.rule.priority || '-'} />
          <WorkspaceField
            label="Cooldown"
            value={row.rule.cooldownDays != null ? `${row.rule.cooldownDays} days` : '-'}
          />
          <WorkspaceField mono label="Next run" value={row.rule.nextRun || '—'} />
          <WorkspaceField mono label="Last fired" value={fmtWhen(row.rule.lastFiredAt)} />

          {isEnabledButNeverFired(row.rule) && (
            <Box className="eo-callout is-warn" sx={{ mt: 1.5, width: '100%' }}>
              <span className="eo-callout__lead">Enabled but never fired: </span>
              this rule is Active, but it has never actually sent. Several event-driven rules have
              no service-layer hook wired yet, so being Active in the console is not the same as
              firing.
            </Box>
          )}

          {row.audience === INTERNAL_AUDIENCE && (
            <Box className="eo-callout is-info" sx={{ mt: 1.5, width: '100%' }}>
              This email goes to a staff inbox, not to users. Pausing the rule stops it; the
              scheduled job checks the rule before every send.
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%', mt: 1.5 }}>
            {row.rule.id && (
              <Button
                disabled={ruleSaving}
                size="small"
                variant="outlined"
                onClick={toggleRulePaused}
              >
                {(row.rule.status ?? '').toUpperCase() === 'ACTIVE' ? 'Pause rule' : 'Resume rule'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/email-campaigns/automation-rules')}
            >
              Edit rule
            </Button>
          </Box>
        </SectionCard>
      )}

      {row.family === 'automation' && !row.rule && (
        <SectionCard icon={<ScheduleOutlinedIcon />} title="No automation rule">
          <Box className="eo-callout is-error" sx={{ width: '100%' }}>
            <span className="eo-callout__lead">No automation rule found for this template. </span>
            This key exists in emailTemplates but has no matching row in email_automation_rules.
            Either nothing fires it, or it is called directly from application code — a few
            automation-category templates are. Its recent sends, on the next tab, settle which.
          </Box>
        </SectionCard>
      )}

      {row.family === 'transactional' && !row.rule && (
        <SectionCard icon={<ScheduleOutlinedIcon />} title="Fired from application code">
          <Box sx={{ width: '100%' }}>
            <Typography color="text.secondary" variant="body2">
              This email is sent directly by application code when the event it belongs to happens.
              There is no rule row behind it.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Typography color="text.secondary" variant="caption">
                TEMPLATE KEY
              </Typography>
              <span className="eo-mono-pill">{row.templateKey}</span>
            </Box>
            <Box className="eo-callout is-info" sx={{ mt: 1.5 }}>
              There is no rule to pause, delay or retarget: &ldquo;when&rdquo; is a line of code,
              not a record. To change when this sends, change the code that calls it.
              {row.audience === INTERNAL_AUDIENCE && ' It goes to a staff inbox, not to users.'}
            </Box>
          </Box>
        </SectionCard>
      )}

      {row.family === 'drip' && (
        <SectionCard icon={<ScheduleOutlinedIcon />} title="Part of a drip sequence">
          <WorkspaceField
            label="Sequence"
            value={dripStepInfo?.sequence.name ?? 'Unknown sequence'}
          />
          <WorkspaceField label="Step" value={row.dripStep != null ? String(row.dripStep) : '-'} />
          <WorkspaceField
            mono
            label="Sends on"
            value={dripStepInfo ? `Day ${dripStepInfo.step.dayOffset}` : '—'}
          />
          <Box className="eo-callout is-info" sx={{ mt: 1.5, width: '100%' }}>
            Drip steps are sent by the scheduler as it walks each person&rsquo;s enrollment, so they
            have no automation rule. The cadence lives with the sequence.
          </Box>
          {dripStepInfo && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(`/email-campaigns/drip-sequences/${dripStepInfo.sequence.campaignType}`)
                }
              >
                Open sequence
              </Button>
            </Box>
          )}
        </SectionCard>
      )}
    </>
  );

  const maxTrend = Math.max(1, ...trend.map((t) => t.sent));
  const performanceTab = (
    <>
      <SectionCard icon={<BarChartOutlinedIcon />} meta="Last 30 days" title="Performance">
        <Box className="eo-kpis" sx={{ width: '100%' }}>
          <Box className="eo-kpi">
            <div className="eo-kpi__label">Sends (30d)</div>
            <div className="eo-kpi__value">{row.sent}</div>
          </Box>
          <Box className="eo-kpi">
            <div className="eo-kpi__label">Open rate</div>
            <div className="eo-kpi__value is-open">{formatRateFraction(row.openRate)}</div>
          </Box>
          <Box className="eo-kpi">
            <div className="eo-kpi__label">Click rate</div>
            <div className="eo-kpi__value is-click">{formatRateFraction(row.clickRate)}</div>
          </Box>
          <Box className="eo-kpi">
            <div className="eo-kpi__label">Status</div>
            <div className="eo-kpi__value">{row.status}</div>
          </Box>
        </Box>
      </SectionCard>

      <SectionCard icon={<BarChartOutlinedIcon />} meta="Last 90 days" title="Sends by week">
        <Box className="eo-trend" sx={{ width: '100%' }}>
          {trend.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No sends recorded in the last 90 days.
            </Typography>
          ) : (
            trend.map((t) => (
              <Box key={t.bucketStart} className="eo-trend__row">
                <span className="eo-trend__label">{formatDayMonth(t.bucketStart)}</span>
                <span className="eo-trend__track">
                  <span
                    className="eo-trend__fill"
                    style={{ width: `${Math.round((t.sent / maxTrend) * 100)}%` }}
                  />
                </span>
                <span className="eo-trend__count">{t.sent}</span>
              </Box>
            ))
          )}
        </Box>
      </SectionCard>
    </>
  );

  const sendsTab = (
    <SectionCard icon={<SendOutlinedIcon />} meta="Last 90 days" title="Recent sends">
      {sends.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {row.status === 'No trigger'
            ? 'No rule fires this template, so nothing has queued yet.'
            : 'This template has not sent in the last 90 days.'}
        </Typography>
      ) : (
        <Box sx={{ width: '100%' }}>
          {sends.map((s) => (
            <Box
              key={s.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
                borderBottom: '1px solid var(--line-soft)',
              }}
            >
              <Typography sx={{ fontFamily: 'var(--font-family-mono)' }} variant="body2">
                {s.userEmail || '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography color={s.openCount > 0 ? 'success.main' : 'text.secondary'} variant="caption">
                  {s.openCount > 0 ? 'Opened' : 'Not opened'}
                </Typography>
                <Typography color={s.clickCount > 0 ? 'primary.light' : 'text.secondary'} variant="caption">
                  {s.clickCount > 0 ? 'Clicked' : 'No click'}
                </Typography>
                <Typography color="text.secondary" sx={{ fontFamily: 'var(--font-family-mono)' }} variant="caption">
                  {fmtWhen(s.sentAt)}
                </Typography>
              </Box>
            </Box>
          ))}
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            variant="outlined"
            onClick={() => navigate('/email-campaigns/send-log')}
          >
            View full send log
          </Button>
        </Box>
      )}
    </SectionCard>
  );

  const tabContent: Record<string, React.ReactNode> = {
    content: contentTab,
    when: whenTab,
    performance: performanceTab,
    sends: sendsTab,
  };

  return (
    <EntityWorkspace
      activeTabId={activeTab}
      activityOpen={activityOpen}
      activityRail={
        row.family === 'automation' || row.rule ? (
          <ActivityRail timeline={timeline} />
        ) : (
          <ActivityRail timeline={[]} />
        )
      }
      backLabel="All emails"
      identityRail={identityRail}
      tabs={TABS}
      onBack={() => navigate('/email-campaigns/emails')}
      onTabChange={setActiveTab}
      onToggleActivity={toggleActivity}
    >
      <Box className="eo-record">{tabContent[activeTab]}</Box>
    </EntityWorkspace>
  );
};

export default EmailRecord;
